import React, {type ReactElement} from 'react'
import {Box} from '../../Box'
import {TableCell} from '../TableCell'
import {TableHead} from '../TableHead'
import {TableRow} from '../TableRow'
import {TableSortLabel} from '../TableSortLabel'
import type {TableHeadCell} from './TableHeadCell'
import type {Order} from './types'

interface SortablePaginatedTableHeadProps<T extends {}> {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof T) => void
  order: Order
  orderBy: keyof T
  headCells: TableHeadCell<T>[]
}

export const SortablePaginatedTableHead = <T extends {}>({
  headCells,
  order,
  orderBy,
  onRequestSort,
}: SortablePaginatedTableHeadProps<T>): ReactElement<SortablePaginatedTableHeadProps<T>> => {
  const onTableLabelClick = (property: keyof T) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  const firstExtraIndex = headCells.findIndex((c) => c.isExtra)

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, index) => {
          const fixed =
            headCell.maxWidth != null || headCell.minWidth != null
              ? {
                  width: headCell.maxWidth,
                  minWidth: headCell.minWidth ?? headCell.maxWidth,
                  maxWidth: headCell.maxWidth,
                }
              : undefined

          const isExtra = !!headCell.isExtra
          const isExtraGroupStart = isExtra && firstExtraIndex === index

          const cellSx =
            isExtra || isExtraGroupStart
              ? {
                  ...(isExtra
                    ? {
                        backgroundColor: 'action.hover',
                        color: 'text.secondary',
                      }
                    : null),
                  ...(isExtraGroupStart
                    ? {
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                      }
                    : null),
                }
              : undefined

          return (
            <TableCell key={String(headCell.id)} sortDirection={orderBy === headCell.id ? order : false} style={fixed} sx={cellSx}>
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={onTableLabelClick(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box
                    sx={{
                      border: 0,
                      clip: 'rect(0 0 0 0)',
                      height: 1,
                      margin: -1,
                      overflow: 'hidden',
                      padding: 0,
                      position: 'absolute',
                      top: 20,
                      width: 1,
                    }}
                  >
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          )
        })}
      </TableRow>
    </TableHead>
  )
}
